import { ColumnDef, Table } from '@tanstack/react-table';
import { BaseCell } from 'src/components/TableV2/cells';
import { IconTagCell } from 'src/components/TableV2/cells/IconTagCell';
import { Content } from 'src/contexts/ContentContext';
import { TableShimmeringDiv } from 'src/tables/Table';

import { SHIMMERING_DIV_HEIGHT_SINGLE_SALE } from '../../SalesTable/SalesTable.type';
import { multiSelectFilter } from '../filters/filterFunctions';

const COLUMN_MIN_SIZE = 50;
const COLUMN_MAX_SIZE = 500;

interface IconTagColumnOptions<TData> {
  /** Column ID */
  id: string;
  /** Content ID for the header label */
  headerContentId: string;
  /** Accessor function to get value from data */
  accessor: (data: TData) => string | null | undefined;
  /** Function to map value to icon/logo component */
  iconMapper: (value: string) => React.ReactNode;
  /** Whether to show text alongside icon (default: true) */
  showText?: boolean;
  /** Column size */
  size?: number;
  /** Min size */
  minSize?: number;
  /** Max size */
  maxSize?: number;
  /** Enable column filter */
  enableColumnFilter?: boolean;
  /** Custom filter function */
  filterFn?: any;
  /** Custom sorting function */
  sortingFn?: any;
  /** Function to determine if cell should render (return false to render null) */
  shouldRender?: (data: TData, table: Table<TData>) => boolean;
}

/**
 * Factory function to create icon tag column definitions.
 * Displays a value with an icon/logo, optionally with text.
 * Less opinionated than createUserTagColumn - you provide the icon mapping.
 *
 * @example
 * // Marketplace/Vendor column with logos
 * createIconTagColumn({
 *   id: 'vendor',
 *   headerContentId: ContentId.Vendor,
 *   accessor: (data) => data?.vendor?.name,
 *   iconMapper: (vendorName) => (
 *     <MarketplaceSquareLogo
 *       marketplace={vendorName as Marketplace}
 *       size={20}
 *     />
 *   ),
 *   showText: true,
 * })
 *
 * @example
 * // Icon-only mode
 * createIconTagColumn({
 *   id: 'status',
 *   accessor: (data) => data?.status,
 *   iconMapper: (status) => <StatusIcon status={status} />,
 *   showText: false,
 * })
 */
export const createIconTagColumn = <TData,>(
  options: IconTagColumnOptions<TData>
): ColumnDef<TData> => {
  const {
    id,
    headerContentId,
    accessor,
    iconMapper,
    showText = true,
    size,
    minSize = COLUMN_MIN_SIZE,
    maxSize = COLUMN_MAX_SIZE,
    enableColumnFilter = false,
    filterFn,
    sortingFn,
    shouldRender,
  } = options;

  // Smart default: use multiSelectFilter if filtering is enabled and no custom filterFn
  const actualFilterFn =
    filterFn ?? (enableColumnFilter ? multiSelectFilter : undefined);

  return {
    id,
    enableColumnFilter,
    ...(actualFilterFn && { filterFn: actualFilterFn }),
    ...(sortingFn && { sortingFn }),
    size,
    minSize,
    maxSize,
    header: () => <Content id={headerContentId} />,
    accessorFn: accessor,
    meta: {
      ...(enableColumnFilter && { columnFilterVariant: 'multiSelect' }),
    },
    cell: function Cell({ getValue, row: { original }, table }) {
      const value = getValue<string | null>();

      if (original == null) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      if (shouldRender && !shouldRender(original, table)) {
        return null;
      }

      if (!value) {
        return <BaseCell />;
      }

      const icon = iconMapper(value);

      if (showText) {
        return <IconTagCell iconLeftContent={icon} text={value} />;
      }

      // Icon-only mode
      return <BaseCell>{icon}</BaseCell>;
    },
  };
};
